import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../context/AuthContext";
import { Plus, Pencil, Trash2, Save, X, RefreshCw, Users, Mail } from "lucide-react";

const STATUSES = [
  { id: "new", label: "New production" },
  { id: "in_production", label: "In production" },
  { id: "completed", label: "Completed" },
];

const empty = { title: "", director: "", description: "", year: new Date().getFullYear(), status: "new", looking_for_roles: "", image_url: "" };

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [viewing, setViewing] = useState(null); // project for which to show interests

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.get(`${API}/admin/projects`, { withCredentials: true });
      setProjects(r.data || []);
    } catch {
      setError("Could not load projects.");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    new: projects.filter(p => p.status === "new").length,
    in_production: projects.filter(p => p.status === "in_production").length,
    completed: projects.filter(p => p.status === "completed").length,
  }), [projects]);

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? All expressions of interest will be removed too.`)) return;
    try { await axios.delete(`${API}/admin/projects/${id}`, { withCredentials: true }); load(); }
    catch { alert("Action failed."); }
  };

  const setStatus = async (id, status) => {
    try { await axios.put(`${API}/admin/projects/${id}`, { status }, { withCredentials: true }); load(); }
    catch { alert("Action failed."); }
  };

  return (
    <div data-testid="admin-tab-projects">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading font-black text-[#0A0A0A] text-2xl tracking-tight">Productions</h2>
          <p className="text-sm text-[#2C2C2C]">Mark projects as new / in production / completed and view crew interest.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={busy} data-testid="admin-projects-refresh" className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={() => setShowAdd(true)} data-testid="admin-projects-add" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-2 text-sm font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add project</button>
        </div>
      </div>

      {error && <div className="bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 mb-4" role="alert">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={projects.length} bg="bg-[#FFD93D]" />
        <Stat label="New" value={counts.new} bg="bg-[#FF2E63]" text="text-[#FFF8E7]" />
        <Stat label="In production" value={counts.in_production} bg="bg-[#1A5DFF]" text="text-[#FFF8E7]" />
        <Stat label="Completed" value={counts.completed} bg="bg-white" />
      </div>

      <div className="space-y-3" data-testid="admin-projects-list">
        {projects.map(p => (
          <Row key={p.id} project={p} onEdit={() => setEditing(p)} onDelete={() => del(p.id, p.title)} onStatus={(s) => setStatus(p.id, s)} onViewInterests={() => setViewing(p)} />
        ))}
        {!busy && projects.length === 0 && <div className="text-[#2C2C2C]">No projects yet.</div>}
      </div>

      {showAdd && <FormModal title="Add a new project" initial={empty} mode="create" onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <FormModal title="Edit project" initial={editing} mode="edit" onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewing && <InterestsModal project={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function Stat({ label, value, bg, text = "text-[#0A0A0A]" }) {
  return (
    <div className={`${bg} ${text} border-2 border-[#0A0A0A] p-4`}>
      <div className="text-xs uppercase tracking-[0.18em] font-bold">{label}</div>
      <div className="font-heading font-black text-3xl mt-1">{value}</div>
    </div>
  );
}

function statusChip(status) {
  if (status === "new") return "bg-[#FF2E63] text-[#FFF8E7]";
  if (status === "in_production") return "bg-[#1A5DFF] text-[#FFF8E7]";
  return "bg-[#0A0A0A] text-[#FFD93D]";
}

function statusLabel(status) {
  return STATUSES.find(s => s.id === status)?.label || status;
}

function Row({ project, onEdit, onDelete, onStatus, onViewInterests }) {
  return (
    <div className="bg-white border-2 border-[#0A0A0A] p-4 md:p-5 flex flex-col lg:flex-row gap-4 items-start" data-testid={`admin-project-${project.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`text-xs uppercase tracking-[0.16em] font-bold px-2 py-0.5 border-2 border-[#0A0A0A] ${statusChip(project.status)}`}>{statusLabel(project.status)}</span>
          <span className="text-xs text-[#2C2C2C] uppercase tracking-[0.14em]">{project.year}</span>
        </div>
        <h4 className="font-heading font-bold text-lg text-[#0A0A0A] leading-tight">{project.title}</h4>
        <div className="text-sm text-[#2C2C2C]">Dir. {project.director}</div>
        <p className="text-sm text-[#2C2C2C] mt-2 line-clamp-2">{project.description}</p>
        {project.looking_for_roles?.length > 0 && (
          <div className="text-xs text-[#2C2C2C] mt-2"><span className="font-bold text-[#1A5DFF] uppercase tracking-[0.14em]">Looking for · </span>{project.looking_for_roles.join(" · ")}</div>
        )}
      </div>
      <div className="flex flex-col gap-2 w-full lg:w-auto">
        <select value={project.status} onChange={(e) => onStatus(e.target.value)} data-testid={`admin-project-status-${project.id}`} className="bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] rounded-none focus:outline-none focus:ring-2 focus:ring-[#FF2E63]">
          {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <div className="flex flex-wrap gap-2">
          <button onClick={onViewInterests} data-testid={`admin-project-interests-${project.id}`} className="bg-[#1A5DFF] text-[#FFF8E7] hover:bg-[#0E4ACC] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Interests</button>
          <button onClick={onEdit} data-testid={`admin-project-edit-${project.id}`} className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
          <button onClick={onDelete} data-testid={`admin-project-delete-${project.id}`} className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
        </div>
      </div>
    </div>
  );
}

function FormModal({ title, initial, mode, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial.title || "",
    director: initial.director || "",
    description: initial.description || "",
    year: initial.year || new Date().getFullYear(),
    status: initial.status || "new",
    looking_for_roles: Array.isArray(initial.looking_for_roles) ? initial.looking_for_roles.join(", ") : (initial.looking_for_roles || ""),
    image_url: initial.image_url || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        looking_for_roles: form.looking_for_roles.split(",").map(t => t.trim()).filter(Boolean),
        image_url: form.image_url || null,
      };
      if (mode === "create") await axios.post(`${API}/admin/projects`, payload, { withCredentials: true });
      else await axios.put(`${API}/admin/projects/${initial.id}`, payload, { withCredentials: true });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="project-form-modal">
      <form onClick={e => e.stopPropagation()} onSubmit={save} className="bg-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow max-w-3xl w-full p-8 my-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-1">{mode === "create" ? "New project" : "Edit"}</div>
            <h2 className="font-heading font-black text-[#0A0A0A] text-2xl">{title}</h2>
          </div>
          <button type="button" onClick={onClose} className="bg-[#0A0A0A] text-[#FFF8E7] hover:bg-[#FF2E63] p-2 border-2 border-[#0A0A0A]"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Project title" required full><input data-testid="admin-project-title" value={form.title} onChange={e => set("title", e.target.value)} required className={cls} /></F>
          <F label="Director" required><input data-testid="admin-project-director" value={form.director} onChange={e => set("director", e.target.value)} required className={cls} /></F>
          <F label="Year" required><input data-testid="admin-project-year" type="number" min="1900" max="2100" value={form.year} onChange={e => set("year", e.target.value)} required className={cls} /></F>
          <F label="Status" required><select data-testid="admin-project-status" value={form.status} onChange={e => set("status", e.target.value)} className={cls} required>{STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}</select></F>
          <F label="Looking for (roles)" hint="Comma-separated — only used when status is 'New production'"><input data-testid="admin-project-roles" value={form.looking_for_roles} onChange={e => set("looking_for_roles", e.target.value)} placeholder="Editor, Sound, Composer" className={cls} /></F>
          <F label="Description" required full><textarea data-testid="admin-project-description" rows={4} value={form.description} onChange={e => set("description", e.target.value)} required className={cls} /></F>
        </div>
        {error && <div className="mt-4 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 text-sm">{error}</div>}
        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-5 py-2.5 text-sm font-medium">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="admin-save-project" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"><Save className="w-4 h-4" /> {submitting ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function InterestsModal({ project, onClose }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const r = await axios.get(`${API}/admin/projects/${project.id}/interests`, { withCredentials: true }); setItems(r.data || []); }
    catch { /* noop */ } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this interest record?")) return;
    try { await axios.delete(`${API}/admin/projects/interests/${id}`, { withCredentials: true }); load(); }
    catch { alert("Failed."); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="interests-modal">
      <div onClick={e => e.stopPropagation()} className="bg-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow max-w-3xl w-full p-8 my-8">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-1">Crew interest</div>
            <h2 className="font-heading font-black text-[#0A0A0A] text-2xl leading-tight">{project.title}</h2>
            <div className="text-sm text-[#2C2C2C]">{items.length} {items.length === 1 ? "person" : "people"} offered to help</div>
          </div>
          <button onClick={onClose} className="bg-[#0A0A0A] text-[#FFF8E7] hover:bg-[#FF2E63] p-2 border-2 border-[#0A0A0A]"><X className="w-4 h-4" /></button>
        </div>

        {busy && <div className="text-[#2C2C2C]">Loading…</div>}
        {!busy && items.length === 0 && <div className="text-[#2C2C2C]">Nobody has expressed interest yet.</div>}

        <div className="space-y-3" data-testid="interests-list">
          {items.map((i) => (
            <div key={i.id} className="bg-white border-2 border-[#0A0A0A] p-4 flex flex-col md:flex-row gap-3 md:items-start justify-between" data-testid={`interest-${i.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-[0.16em] font-bold px-2 py-0.5 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A]">{i.role_offered}</span>
                  <span className="text-xs text-[#2C2C2C]">{new Date(i.created_at).toLocaleString()}</span>
                </div>
                <div className="font-heading font-bold text-[#0A0A0A]">{i.name}</div>
                <a href={`mailto:${i.email}?subject=Re:%20${encodeURIComponent(project.title)}`} className="text-sm text-[#0A0A0A] underline inline-flex items-center gap-1 hover:text-[#FF2E63]"><Mail className="w-3.5 h-3.5" /> {i.email}</a>
                {i.message && <p className="mt-2 text-sm text-[#2C2C2C] whitespace-pre-wrap">{i.message}</p>}
              </div>
              <button onClick={() => del(i.id)} data-testid={`interest-delete-${i.id}`} className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 self-start"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const cls = "w-full bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none text-sm";

function F({ label, hint, children, required, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.16em] font-bold text-[#0A0A0A]">{label} {required && <span className="text-[#FF2E63]">*</span>}</span>
      {children}
      {hint && <span className="text-xs text-[#2C2C2C]">{hint}</span>}
    </label>
  );
}
